import React, { Component } from 'react';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { reduxForm } from 'redux-form';
import { Logger } from '../../utils/Logger';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { View, StyleSheet, ActivityIndicator } from 'react-native';

import Theme, { ThemeType } from '../../theme';
import { PartnerList, BookingFlowFooter } from '../components';
import { FormError, SearchPartner, partnerInvitedToast, showToastMessage } from '../../ui';

import { getNextBookingStep, setWorkoutSession, getPreviousBookingStep, selectStudioPartner } from '../actions';
import { changeInviteSuccess } from '../../Planner/plannerActions';
import { i18n } from '../../utils';
import { ProcessTypes } from 'fitbeat.gym-core';
import { getGymTimezone } from '../../utils/gyms';

class PartnerSearch extends Component {
    constructor(props) {
        super(props);

        const gymTimezone = getGymTimezone(props.workoutSession);
        this.state = {
            partners: [],
            whitelistPartners: [],
            hasSearched: false,
            isLoading: false,
            error: null,
            gymTimezone,
        };
    }

    componentDidMount = async () => {
        this.props.selectStudioPartner(null);
        await this.fetchWhitelistPartners();
    }

    fetchWhitelistPartners = async () => {
        try {
            const {
                workoutSession,
                pendingSlot,
                currentSelectedGymName,
                userProfileService: { getAvailabilityForWhitelistPartners },
            } = this.props;

            let workoutType;
            let startTime;

            if (workoutSession) {
                workoutType = workoutSession.workoutType;
                startTime = workoutSession.startTime;
            } else {
                workoutType = pendingSlot.workoutType;
                startTime = pendingSlot.startTime;
            }
            this.setIsLoading(true);
            const searchedPartners = await getAvailabilityForWhitelistPartners(
                workoutType,
                new Date(startTime).getTime(),
                currentSelectedGymName,
            );
            await this.setState({ whitelistPartners: searchedPartners });
        } catch (error) {
            Logger.notify(error);
        } finally {
            this.setIsLoading(false);
        }
    }

    fetchParters = async (partnerIdentifier) => {
        try {
            const searchedPartners = await this.searchPartner(partnerIdentifier);
            await this.setState({ partners: searchedPartners, hasSearched: true });
        } catch (error) {
            Logger.notify(error);
        }
    }

    searchPartner = async (partnerIdentifier) => {
        const {
            workoutSession,
            pendingSlot,
            currentSelectedGymName,
            userProfileService: { searchPartner },
        } = this.props;
        try {
            let workoutType;
            let startTime;
            if (workoutSession) {
                workoutType = workoutSession.workoutType;
                startTime = workoutSession.startTime;
            } else {
                workoutType = pendingSlot.workoutType;
                startTime = pendingSlot.startTime;
            }

            this.setIsLoading(true);
            this.clearError();
            const partners = await searchPartner(
                partnerIdentifier,
                workoutType,
                new Date(startTime).getTime(),
                currentSelectedGymName,
            );
            await this.setState({ partners, hasSearched: true });
        } catch (error) {
            this.setError(error);
            Logger.notify(error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    handelInvite = async (partner) => {
        const { selectStudioPartner } = this.props;
        selectStudioPartner(partner);
    }

    handleNext = () => {
        const { workoutSession, userProfile, getNextBookingStep, studioPartner } = this.props;
        if (workoutSession) {
            this.changeInvite(studioPartner);
        } else {
            getNextBookingStep(userProfile);
        }
    }

    changeInvite = async (partnerId) => {
        const {
            day,
            workoutSessionService: { sendInvite },
            getNextBookingStep,
            workoutSession,
            userProfile,
            setWorkoutSession,
        } = this.props;
        try {
            this.clearError();
            this.setIsLoading(true);
            const changedWorkoutSession = await sendInvite(partnerId.uid, workoutSession.uniqueReference);
            this.props.showToastMessage(
                partnerInvitedToast(partnerId.fullname, moment(workoutSession.startTimeOfFirstExercise).format('dddd, Do MMMM')));
            this.props.changeInviteSuccess(workoutSession.uniqueReference, changedWorkoutSession);
            setWorkoutSession(changedWorkoutSession);
            getNextBookingStep(userProfile, ProcessTypes.BookingChanged);
        } catch (error) {
            this.setError(error);
            Logger.notify(error);
        } finally {
            this.setIsLoading(false);
        }
    }

    setIsLoading = async (loading) => {
        this.setState({ isLoading: loading });
    }

    setError = (apiError) => {
        this.setState({ apiError });
    }

    clearError = () => {
        this.setError(null);
    }

    onInviteComplete = (partner, slot) => {
        this.props.onInviteComplete(partner, slot);
    }

    getPartnerListToDisplay = () => {
        const { partners, whitelistPartners, hasSearched } = this.state;

        if (hasSearched && partners.length === 0) {
            return [];
        } else if (partners.length === 0) {
            return whitelistPartners;
        }

        return partners;
    }

    onSearchInputChange = (value) => {
        if (value.length === 0) {
            this.setState({ partners: [] });
        }
    }

    render() {
        const { t, getPreviousBookingStep, studioPartner } = this.props;
        const {
            hasSearched,
            isLoading,
            apiError,
        } = this.state;

        return (
            <View style={{ flex: 1 }}>
                <View style={styles.formContainer}>
                    <SearchPartner
                        theme={ThemeType.light}
                        logger={Logger}
                        isLoading={isLoading}
                        onSearch={this.searchPartner}
                        onSearchInputChange={this.onSearchInputChange}
                    />
                </View>

                {isLoading &&
                    <ActivityIndicator
                        size="small"
                        color={Theme.PRIMARY}
                        style={styles.activityIndicator}
                    />
                }

                <PartnerList
                    t={t}
                    partners={this.getPartnerListToDisplay()}
                    isLoading={isLoading}
                    hasSearched={hasSearched}
                    handelInvite={this.handelInvite}
                    selectedPartner={studioPartner}
                />

                <View>
                    <FormError error={apiError} />
                </View>
                <BookingFlowFooter style={styles.footer}>
                    <Button
                        fontColor={Theme.PRIMARY}
                        size={ButtonSize.MEDIUM}
                        buttonType={ButtonTypes.OUTLINE}
                        title={i18n.t('bookings:back')}
                        onPress={() => getPreviousBookingStep()}
                        containerStyle={styles.backContainer}
                    />

                    <Button
                        fontColor={Theme.WHITE}
                        size={ButtonSize.MEDIUM}
                        disabled={!studioPartner}
                        title={i18n.t('bookings:sendInvite')}
                        onPress={this.handleNext}
                        containerStyle={styles.nextContainer}
                    />
                </BookingFlowFooter>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    subHeader: {
        fontSize: 16,
        marginBottom: 10,
        color: Theme.CHARCOAL,
    },
    formContainer: {
        paddingVertical: 5,
    },
    formLabel: {
        fontSize: 15,
        fontFamily: Theme.Custom600,
        paddingVertical: 5,
        color: Theme.GREY_MEDIUM,
    },
    footer: {
        flexDirection: 'row',
    },
    backContainer: {
        flex: 0.3,
    },
    nextContainer: {
        flex: 0.7,
        marginLeft: 5,
    },
});

const mapStateToProps = (state) => {
    return {
        workoutSession: state.bookings.bookingWizard.workoutSession,
        pendingSlot: state.bookings.bookingWizard.pendingSlot,
        currentSelectedGymName: state.bookings.bookingWizard.currentSelectedGymName,
        studioPartner: state.bookings.bookingWizard.studioPartner,
    };
}

const mapDispatchToProps = (dispatch) => {
    return {
        showToastMessage: (toast) => dispatch(showToastMessage(toast)),
        getNextBookingStep: (userProfile, processType) => dispatch(getNextBookingStep(userProfile, processType)),
        setWorkoutSession: (booking) => dispatch(setWorkoutSession(booking)),
        getPreviousBookingStep: () => dispatch(getPreviousBookingStep()),
        selectStudioPartner: (partner) => dispatch(selectStudioPartner(partner)),
        changeInviteSuccess: (workoutId, workoutSession) => dispatch(changeInviteSuccess(workoutId, workoutSession)),
    };
};

PartnerSearch = connect(mapStateToProps, mapDispatchToProps)(PartnerSearch);
PartnerSearch = reduxForm({ form: 'ChangePasswordForm' })(PartnerSearch);
PartnerSearch = translate(['bookings'], { wait: true })(PartnerSearch);
export default PartnerSearch;
